import React from "react";
import {
  formatSecondsShort,
  formatTimeLabel,
  getDistributionTimezone,
  type DistributionKpis,
} from "../../services/peopleDistributionTypes";
import { DistributionKpiCard } from "./DistributionKpiCard";

type DistributionKpiSectionProps = {
  kpis: DistributionKpis | null;
};

export const DistributionKpiSection: React.FC<DistributionKpiSectionProps> = ({ kpis }) => {
  const timezone = getDistributionTimezone();

  if (!kpis) {
    return (
      <div className="flex min-h-[120px] items-center justify-center rounded-xl border border-dashed border-slate-200 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
        No KPI data available.
      </div>
    );
  }

  const currentParts: string[] = [];
  if (kpis.currentStudentCount) {
    currentParts.push(`${kpis.currentStudentCount} stu`);
  }
  if (kpis.currentStaffCount) {
    currentParts.push(`${kpis.currentStaffCount} staff`);
  }
  if (kpis.currentSecurityGuardCount) {
    currentParts.push(`${kpis.currentSecurityGuardCount} guard`);
  }
  if (kpis.currentPersonCount) {
    currentParts.push(`${kpis.currentPersonCount} person`);
  }
  const breakdownTotal =
    (kpis.currentStudentCount ?? 0) +
    (kpis.currentStaffCount ?? 0) +
    (kpis.currentSecurityGuardCount ?? 0) +
    (kpis.currentPersonCount ?? 0);
  const currentSubtitle =
    currentParts.length && breakdownTotal === kpis.currentOccupancy
      ? currentParts.join(" · ")
      : undefined;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <DistributionKpiCard
        label="Current"
        value={kpis.currentOccupancy}
        subtitle={currentSubtitle ?? "Live concurrent"}
        accent="blue"
      />
      <DistributionKpiCard
        label="Peak"
        value={kpis.peakOccupancy}
        subtitle={`Peak concurrent · at ${formatTimeLabel(kpis.peakTime, timezone)}`}
        accent="red"
      />
      <DistributionKpiCard
        label="Peak Avg Dwell"
        value={formatSecondsShort(kpis.peakAvgDwell)}
        subtitle={`at ${formatTimeLabel(kpis.peakAvgDwellTime, timezone)}`}
        accent="red"
      />
      <DistributionKpiCard
        label="Average Avg Dwell"
        value={formatSecondsShort(kpis.avgDwell)}
        subtitle="weighted Σ"
        accent="gray"
      />
    </div>
  );
};

export default DistributionKpiSection;
